import { computed, ref, watch } from '@nuxtjs/composition-api';
import { isEmpty, omit } from 'lodash-es';
import { ProductData } from './product';
import { useRouter } from './router';
import { useProductContext } from './productContext';
import { serializeObjectToParams } from '~/utils/route';
/**
 * @description composition function to handle all configurable products options
 * @returns
 */
export function useProductConfigurationOptions(quickView: Boolean = false) {
  const { product, productCtx, appendProductValues } = useProductContext();

  const selectedOptions = ref<{ [code: string]: string }>();

  const { redirect, route, query } = useRouter();

  const productMap = ref(new Map<string, string[]>());

  const selectedVariant = computed(() => {
    const variantSlug = route.value.params.pathMatch ? route.value.params.pathMatch.split('/').pop() : '';
    return productCtx.value?.variants?.find(variant => variant.slug === variantSlug) || null;
  });

  const unwatch = watch(
    productCtx,
    newProduct => {
      if (newProduct) {
        productMap.value = generateMap(
          productCtx.value,
          productCtx.value.configurationOptions?.map(option => option?.attribute_code || '') || []
        );
        setTimeout(() => unwatch());
      }
    },
    {
      immediate: true,
    }
  );

  const productOptions = computed(() => {
    return productCtx.value?.configurationOptions?.map(option => {
      return {
        ...option,
        values: option?.values?.map(value => {
          return {
            ...value,
            active:
              isEmpty(selectedOptions.value) ||
              (selectedOptions.value?.[option?.attribute_code ?? ''] &&
                Object.values(selectedOptions.value).find(option => value?.uid === option))
                ? true
                : isContainItems(
                    [
                      ...Object.values(
                        omit(
                          selectedOptions.value,
                          selectedOptions.value?.[option?.attribute_code ?? ''] ? option?.attribute_code ?? '' : ''
                        )
                      ),
                      value?.uid || '',
                    ],
                    Array.from(productMap.value.values())
                  ),
          };
        }),
      };
    });
  });

  watch(
    selectedVariant,
    newSelectedVariant => {
      if (!newSelectedVariant) {
        appendProductValues({
          name: productCtx.value.name,
          price: productCtx.value.price,
          parentSku: undefined,
        });

        return;
      }

      if (newSelectedVariant) {
        appendProductValues({
          sku: newSelectedVariant.sku,
          name: newSelectedVariant.name,
          price: newSelectedVariant.price,
          priceBefore: newSelectedVariant.priceBefore,
          media_gallery: newSelectedVariant.media_gallery,
          thumbnail: newSelectedVariant.thumb,
          parentSku: productCtx.value.sku ?? '',
          type: newSelectedVariant.type,
          stock: newSelectedVariant.stock,
          hasGift: Boolean(newSelectedVariant.hasGift),
          gift: newSelectedVariant.gift,
        });

        selectedOptions.value = {
          ...selectedOptions.value,
          ...newSelectedVariant.attributes?.reduce(
            (accu, curr) => ({ ...accu, [curr?.code ?? '']: curr?.uid || '' }),
            {}
          ),
        };
      }
    },
    {
      immediate: true,
    }
  );

  watch(
    selectedOptions,
    options => {
      if (!options && !quickView) {
        redirect(
          `/${product.value?.url_key}/var/${product.value.variants?.[0]?.slug}${serializeObjectToParams(query.value)}`
        );
        return;
      }

      if (options && !Object.keys(options).length) {
        redirect(`/${product.value?.url_key}${serializeObjectToParams(query.value)}`);

        return;
      }

      if (options) {
        const matchedVariant = product.value?.variants?.find(variant => {
          return Object.keys(options).every(option => {
            return variant.attributes?.find(attribute => attribute?.uid === selectedOptions.value?.[option]);
          });
        });

        if (quickView && matchedVariant) {
          appendProductValues({
            sku: matchedVariant.sku,
            name: matchedVariant.name,
            price: matchedVariant.price,
            priceBefore: matchedVariant.priceBefore,
            media_gallery: matchedVariant.media_gallery,
            thumbnail: matchedVariant.thumb,
            parentSku: productCtx.value.sku ?? '',
            type: matchedVariant.type,
            stock: matchedVariant.stock,
            hasGift: Boolean(matchedVariant.hasGift),
            gift: matchedVariant.gift,
          });
          return;
        }

        if (matchedVariant) {
          redirect(`/${product.value?.url_key}/var/${matchedVariant?.slug}${serializeObjectToParams(query.value)}`);
        }
      }
    },
    {
      immediate: true,
    }
  );

  watch(product, newProduct => {
    if (newProduct.type === 'ConfigurableProduct' && quickView && newProduct.variants?.length) {
      const firstVariant = newProduct.variants[0];
      selectedOptions.value = {
        ...selectedOptions.value,
        ...firstVariant.attributes?.reduce((accu, curr) => ({ ...accu, [curr?.code ?? '']: curr?.uid || '' }), {}),
      };

      return;
    }

    if (newProduct.type === 'ConfigurableProduct' && !selectedOptions.value) {
      redirect(
        `/${product.value?.url_key}/var/${product.value.variants?.[0]?.slug}${serializeObjectToParams(query.value)}`
      );
    }
  });

  return {
    productOptions,
    selectedOptions,
    route,
    selectedVariant,
  };
}

function generateMap(product: ProductData, allowedAttributes: string[] | []): Map<string, string[]> {
  const result = new Map<string, string[]>();

  product.variants?.forEach(variant => {
    const uids = variant.attributes
      ?.filter(attribute => attribute?.code && allowedAttributes.find(attr => attr === attribute?.code))
      ?.map(attribute => attribute?.uid || '');

    result.set(variant?.id?.toString() || '', uids || []);
  });
  return result;
}

function isContainItems(op: string[], index: string[][]): boolean {
  return index.some(item => op.every(existedProperty => item.includes(existedProperty)));
}
